import { useState, RefObject, useRef, useCallback, memo, useEffect } from 'react';
import { token } from '@atlaskit/tokens';
import { css } from '@emotion/react';
import { Icon } from '@components/shared/Icon';

import { CardStatus } from '@components/learning/LearningSidebar/constants';
import { HoverInfo, useSidebarContext } from '@components/learning/LearningSidebar/context/LearningSidebarContext';

import { LessonCard } from './LessonCard';
import { LessonsSkeletonLoader } from './skeletons/LessonsSkeletonLoader';
import { Appearance, TextTooltip } from './TextTooltip';
import { isEntitySelected } from '../utils/utils';
import { CourseItem, ProgressForLessons } from '../types';
import { CourseProgressCircle } from './CourseProgressCircle';

const COURSE_CARD_TITLE = {
  [CardStatus.COMPLETED]: 'Completed',
  [CardStatus.DEFAULT]: 'Course',
};

export interface CourseCardProps {
  course: CourseItem;
  status: CardStatus;
  isLoading: boolean;
  expandedLessonIds?: string[];
  expandedCourseIds?: string[];
  progressForLessons?: ProgressForLessons;
  selectedSectionId?: string;
  currentLessonSlug?: string;
  onHover: (data: HoverInfo) => void;
  onMouseLeave: (e: React.MouseEvent<HTMLDivElement>, id: string, courseCardRef: RefObject<HTMLDivElement>) => void;
  onSelect: (courseSlug: string, lessonSlug?: string, sectionSlug?: string) => void;
  scrollContainerRef: RefObject<HTMLDivElement>;
  popupRef: RefObject<HTMLDivElement>;
}

interface CourseCardPropsWithRef extends CourseCardProps {
  selectedLessonRef?: RefObject<HTMLDivElement>;
}

interface CourseCardHeaderContentProps {
  isCourseExpanded: boolean;
  isSelected: boolean;
  status: CardStatus;
  title: string;
}

const CourseCardHeaderContent: React.FC<CourseCardHeaderContentProps> = ({
  isCourseExpanded,
  isSelected,
  status,
  title,
}) => (
  <>
    <div css={[courseExpanderStyles, isCourseExpanded && courseExpanderSelectedStyles]}>
      <Icon
        type={isCourseExpanded ? 'chevron-down' : 'chevron-right'}
        color={isCourseExpanded ? token('color.icon.selected') : token('color.icon')}
      />
    </div>
    <div css={courseTitleContainerStyles}>
      <p
        css={[
          courseCardTitleStyles,
          status === CardStatus.COMPLETED && courseCardTitleCompletedStyles,
          status === CardStatus.DEFAULT && courseCardTitleDefaultStyles,
          isSelected && courseCardTitleSelectedStyles,
        ]}
      >
        {isSelected ? 'Current course'.toUpperCase() : COURSE_CARD_TITLE[status].toUpperCase()}
      </p>
      <TextTooltip text={title} isSelected={isSelected} appearance={Appearance.COURSE} />
    </div>
  </>
);

export const CourseCard: React.FC<Omit<CourseCardPropsWithRef, 'onHover' | 'onMouseLeave' | 'popupRef'>> = (props) => {
  const { handleMouseEnter, handleMouseLeave, popupRef } = useSidebarContext();

  const mouseLeaveHandler = useCallback(
    (e: React.MouseEvent<HTMLDivElement>, id: string, courseCardRef: RefObject<HTMLDivElement>) => {
      const lessonPage = document.getElementById('lesson-page-body');
      const isMovedToSidebar = (e.relatedTarget as HTMLElement).tagName === 'ASIDE';

      if (
        isMovedToSidebar ||
        e.relatedTarget === props.scrollContainerRef.current ||
        lessonPage?.contains(e.relatedTarget as HTMLElement)
      ) {
        handleMouseLeave();
        return;
      }

      if (e.currentTarget === courseCardRef.current || e.relatedTarget === popupRef.current) {
        return;
      }

      handleMouseLeave(id);
    },
    [popupRef],
  );

  return (
    <CourseCardComponent {...props} onHover={handleMouseEnter} onMouseLeave={mouseLeaveHandler} popupRef={popupRef} />
  );
};

export const CourseCardComponent: React.FC<CourseCardPropsWithRef> = memo(
  ({
    course: { sys, title, reward, slug, lessonsCollection },
    status,
    isLoading,
    selectedLessonRef,
    progressForLessons,
    onHover,
    onMouseLeave,
    onSelect,
    expandedLessonIds = [],
    expandedCourseIds = [],
    selectedSectionId,
    currentLessonSlug,
  }) => {
    const courseCardRef = useRef<HTMLDivElement>(null);
    const isSelected = isEntitySelected(slug);

    const [isCourseExpanded, setIsCourseExpanded] = useState(false);

    useEffect(() => {
      const isExpanded = expandedCourseIds.includes(sys.id);

      setIsCourseExpanded(isExpanded || false);
    }, [expandedCourseIds]);

    const handleCourseCardExpand = useCallback(() => {
      setIsCourseExpanded(!isCourseExpanded);
      onSelect(slug);
    }, [isCourseExpanded, onSelect, slug]);

    const showCoursePopup = useCallback(() => {
      if (courseCardRef.current) {
        onHover({ courseId: sys.id, courseCardPosition: courseCardRef.current.getBoundingClientRect() });
      }
    }, [sys.id, onHover]);

    const hideCoursePopup = useCallback(
      (e: React.MouseEvent<HTMLDivElement>) => {
        onMouseLeave(e, sys.id, courseCardRef);
      },
      [onMouseLeave, sys.id, courseCardRef],
    );

    return (
      <div
        data-testid="sidebar-course-card"
        css={[courseCardStyles, isCourseExpanded && courseCardOpenedStyles]}
        ref={courseCardRef}
        onMouseEnter={showCoursePopup}
        onMouseLeave={hideCoursePopup}
      >
        <div
          css={[courseCardHeaderStyles, isCourseExpanded && courseCardHeaderOpenedStyles]}
          onClick={handleCourseCardExpand}
          onMouseEnter={showCoursePopup}
          data-testid="course-card-header"
        >
          <CourseCardHeaderContent
            isCourseExpanded={isCourseExpanded}
            isSelected={isSelected}
            status={status}
            title={title}
          />
          <CourseProgressCircle
            lessonsCollection={lessonsCollection}
            progressForLessons={progressForLessons}
            reward={reward}
            title={title}
          />
        </div>
        <div css={lessonContainerStyles} onMouseEnter={hideCoursePopup}>
          {isLoading && isCourseExpanded ? (
            <LessonsSkeletonLoader />
          ) : (
            isCourseExpanded &&
            lessonsCollection.items.map((lesson) => (
              <div key={lesson.sys.id} ref={isEntitySelected('', lesson.slug) ? selectedLessonRef : null}>
                <LessonCard
                  lesson={lesson}
                  courseSlug={slug}
                  progressForLesson={progressForLessons?.lessons.find(
                    (lessonProgress) => lessonProgress.lessonId === lesson.sys.id,
                  )}
                  onSelect={onSelect}
                  isLoading={isLoading}
                  expandedLessonIds={expandedLessonIds}
                  selectedSectionId={selectedSectionId}
                  currentLessonSlug={currentLessonSlug}
                />
              </div>
            ))
          )}
        </div>
      </div>
    );
  },
);

const courseCardStyles = css({
  display: 'flex',
  width: '100%',
  flexDirection: 'column',
  paddingTop: token('space.300'),
  paddingBottom: token('space.300'),
  borderBottom: `1px solid ${token('color.border')}`,

  '&:hover': {
    background: '#F7F8F9',
  },
});

const courseCardOpenedStyles = css({
  paddingBottom: 0,

  '&:hover': {
    background: 'none',
  },
});

const courseCardHeaderStyles = css({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  cursor: 'pointer',
  paddingLeft: token('space.100'),
  paddingRight: token('space.100'),
});

const courseCardHeaderOpenedStyles = css({
  marginBottom: token('space.100'),
});

const courseExpanderStyles = css({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '24px',
  height: '24px',
  borderRadius: '4px',
  border: `1px solid ${token('color.background.accent.gray.subtlest')}`,
  cursor: 'pointer',
});

const courseExpanderSelectedStyles = css({
  border: `1px solid ${token('color.background.accent.blue.subtler')}`,
});

const courseTitleContainerStyles = css({
  display: 'flex',
  flexDirection: 'column',
  marginLeft: token('space.100'),
  marginRight: 'auto',
});

const courseCardTitleStyles = css({
  fontFamily: 'Charlie Display, sans-serif',
  fontSize: '12px',
  fontWeight: 600,
  lineHeight: '16px',
});

const courseCardTitleSelectedStyles = css({
  color: token('color.text.selected'),
});

const courseCardTitleCompletedStyles = css({
  color: token('color.text.success'),
});

const courseCardTitleDefaultStyles = css({
  color: token('color.text.subtlest'),
});

const lessonContainerStyles = css({
  display: 'flex',
  flexDirection: 'column',
  gap: token('space.100'),
});
